import React, { useState } from "react";
import { motion } from "framer-motion";
import { Flower2, Heart, Check } from "lucide-react";
import {
  Button,
  Card,
  Input,
  Typography,
} from "@material-tailwind/react";
import Layout from "./Layout/Layout";

const DonationPage_RadhaKrishna = () => {
  const [customAmount, setCustomAmount] = useState("");
  const [selectedAmount, setSelectedAmount] = useState(null);

  const donationTiers = [
    { amount: 501, title: "Bal Vidya Seva", desc: "Provides educational books, stationery kits, and coaching notebooks for one village child." },
    { amount: 1001, title: "Annadaan Seva", desc: "Provides healthy, sanctified prasadam meals for 10 local village children." },
    { amount: 2100, title: "Sadhu Seva", desc: "Sponsors one full Sunday feast program and prasadam distribution in the community." },
    { amount: 5100, title: "Archan Seva", desc: "Supports daily deity worship, altar flowers, and temple maintenance for one week." },
    { amount: 11000, title: "Gram Vikas Patron", desc: "Sponsors a village child's annual education coaching, computer training, and moral classes." },
  ];

  const handleDonate = () => {
    const amount = Number(selectedAmount || customAmount);
    if (!amount || isNaN(amount) || amount <= 0) {
      alert("Please enter a valid positive donation amount.");
      return;
    }
    alert(`Thank you for your kind seva support of ₹${amount}! Please complete your contribution using the Bank details or QR code below. 🙏`);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#fffbf4] to-[#fff6e6] flex flex-col items-center py-16 px-6">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mb-12"
        >
          <div className="flex justify-center mb-5">
            <Flower2 className="text-amber-500 w-12 h-12 animate-pulse" />
          </div>
          <span className="text-amber-800 font-semibold text-xs tracking-[0.25em] uppercase mb-2 block">
            Make A Difference
          </span>
          <h1 className="text-3xl sm:text-5xl font-bold text-amber-950 mb-5 font-serif tracking-wide leading-tight">
            Offer Your Seva
          </h1>
          <p className="text-stone-600 text-base sm:text-lg leading-relaxed font-light">
            Radha Krishna Gram Vikas Kendra is dedicated to rural upliftment, children's values education, and spreading spiritual teachings. Your kind seva helps nurture and transform lives with care and devotion.
          </p>
        </motion.div>

        {/* DONATION CARD */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="w-full max-w-6xl mb-12"
        >
          <div className="bg-white border border-amber-100 shadow-xl rounded-3xl p-6 sm:p-10">
            <h2 className="text-2xl text-center text-amber-950 font-serif font-bold mb-10 tracking-wide">
              Select a Seva Initiative
            </h2>

            {/* Donation Tiers */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-10">
              {donationTiers.map((tier) => {
                const isSelected = selectedAmount === tier.amount;
                return (
                  <motion.div
                    key={tier.amount}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedAmount(tier.amount);
                      setCustomAmount("");
                    }}
                    className={`cursor-pointer border-2 p-5 rounded-2xl flex flex-col justify-between min-h-[200px] transition-all duration-300 ${
                      isSelected
                        ? "border-amber-600 bg-amber-50/50 shadow-md"
                        : "border-amber-100/70 bg-white hover:border-amber-400"
                    }`}
                  >
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-[#ffd27f] bg-amber-900 font-bold text-[10px] px-2.5 py-1 rounded-full uppercase tracking-wider">
                          Seva
                        </span>
                        {isSelected && (
                          <div className="p-1 bg-amber-600 text-white rounded-full">
                            <Check size={12} />
                          </div>
                        )}
                      </div>
                      <h3 className="font-serif font-bold text-amber-950 text-base mb-1">
                        {tier.title}
                      </h3>
                      <p className="text-[11px] text-stone-500 font-light leading-normal line-clamp-4">
                        {tier.desc}
                      </p>
                    </div>
                    <div className="mt-4 border-t border-amber-100/50 pt-3">
                      <p className="text-xl font-bold text-amber-700">
                        ₹{tier.amount}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Custom Donation Input */}
            <div className="flex flex-col items-center space-y-6 pt-6 border-t border-amber-100/50">
              <div className="relative w-full max-w-sm">
                <Input
                  type="number"
                  label="Enter Custom Seva Amount (₹)"
                  min="1"
                  value={customAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value >= 0) setCustomAmount(value);
                    setSelectedAmount(null);
                  }}
                  className="text-center text-lg font-medium focus:border-amber-500 focus:ring-amber-500/20"
                />
                {customAmount < 0 && (
                  <p className="text-red-500 text-xs mt-1 text-center">
                    Amount cannot be negative.
                  </p>
                )}
              </div>

              <Button
                onClick={handleDonate}
                size="lg"
                ripple={true}
                className="w-full max-w-sm bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold text-sm tracking-wider uppercase py-4 flex items-center justify-center gap-2 rounded-xl shadow-md hover:shadow-lg transition-all"
              >
                <Heart className="w-4 h-4 text-white fill-white" />
                Confirm Seva Contribution
              </Button>

              <p className="text-stone-500 text-xs max-w-md leading-relaxed text-center italic mt-2">
                “Your contribution sustains our efforts to bring education, health, and hope to rural Bharat. May Lord Krishna bless you for your kindness.” 🙏
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default DonationPage_RadhaKrishna;
